/**
 * CoachDirectory — public, SEO-indexable index of all opt-in coach profiles.
 *
 * Mounted at `/coaches`. Lightweight: fetches `/api/coach/directory` paginated.
 * Fully translated EN/ES/FR with hreflang annotations.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { Users, BookOpen, Heart, ArrowRight } from 'lucide-react';
import { useLang } from '@/context/LangContext';

const API = process.env.REACT_APP_BACKEND_URL;
const LANG_LOCALE = { es: 'es-ES', en: 'en-US', fr: 'fr-FR' };
const OG_LOCALE = { es: 'es_ES', en: 'en_US', fr: 'fr_FR' };
const PLAYS_PATH = { es: '/jugadas', en: '/plays', fr: '/jeux' };

const fmt = (n, lang) => Number(n || 0).toLocaleString(LANG_LOCALE[lang] || 'es-ES');

function CoachCard({ c, lang }) {
  return (
    <Link
      to={`/coach/${c.public_slug}`}
      data-testid={`directory-card-${c.public_slug}`}
      className="group flex gap-4 rounded-xl border border-gold/15 bg-navy-deep p-4 hover:border-gold/60 transition-all"
    >
      {c.avatar_url ? (
        <img src={c.avatar_url} alt={c.name} className="w-16 h-16 rounded-lg border border-gold/30 object-cover shrink-0" />
      ) : (
        <div className="w-16 h-16 rounded-lg border border-gold/30 bg-navy flex items-center justify-center font-h text-2xl text-gold shrink-0">
          {(c.name || 'C').charAt(0)}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <h3 className="font-h text-lg leading-tight text-white group-hover:text-gold truncate">{c.name}</h3>
        {c.country && <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mt-0.5">{c.country}</div>}
        {c.bio && <p className="mt-1 text-xs text-slate-400 line-clamp-2">{c.bio}</p>}
        <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-400">
          <span className="inline-flex items-center gap-1"><BookOpen className="w-3 h-3 text-gold" /> {fmt(c.stats?.plays, lang)}</span>
          <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3 text-rose-400" /> {fmt(c.stats?.likes, lang)}</span>
        </div>
      </div>
      <ArrowRight className="w-4 h-4 text-gold/40 self-center group-hover:text-gold transition-colors" />
    </Link>
  );
}

export default function CoachDirectory() {
  const { lang, t } = useLang();
  const [coaches, setCoaches] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    axios.get(`${API}/api/coach/directory?limit=48`)
      .then((r) => { setCoaches(r.data?.coaches || []); setTotal(r.data?.total || 0); })
      .finally(() => setLoading(false));
  }, []);

  const playsBase = PLAYS_PATH[lang] || '/jugadas';

  return (
    <div className="min-h-screen bg-navy text-white" data-testid="coach-directory-page">
      <Helmet>
        <title>{`${t('cp_dir_coaches_label')} | PlayMaster IA`}</title>
        <meta name="description" content={t('cp_seo_dir_desc')} />
        <link rel="canonical" href="https://playmasteria.com/coaches" />
        <link rel="alternate" hrefLang="es" href="https://playmasteria.com/coaches" />
        <link rel="alternate" hrefLang="en" href="https://playmasteria.com/coaches" />
        <link rel="alternate" hrefLang="fr" href="https://playmasteria.com/coaches" />
        <link rel="alternate" hrefLang="x-default" href="https://playmasteria.com/coaches" />
        <meta property="og:title" content={`${t('cp_dir_coaches_label')} | PlayMaster IA`} />
        <meta property="og:description" content={t('cp_seo_dir_desc')} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={OG_LOCALE[lang] || 'es_ES'} />
      </Helmet>

      <header className="sticky top-0 z-40 bg-navy-deep/95 backdrop-blur border-b border-gold/20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="PlayMaster IA" className="w-12 h-12 sm:w-14 sm:h-14" />
            <span className="font-h text-lg sm:text-xl uppercase tracking-widest">PlayMaster IA</span>
          </Link>
          <nav className="flex items-center gap-3 text-xs font-mono uppercase tracking-widest">
            <Link to={playsBase} className="text-slate-300 hover:text-gold">{t('ph_nav_jugadas')}</Link>
            <Link to="/blog" className="text-slate-300 hover:text-gold">{t('ph_nav_blog')}</Link>
            <Link to="/login" className="text-slate-300 hover:text-gold">{t('ph_nav_login')}</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-10">
        <section className="mb-8">
          <div className="flex items-baseline gap-3">
            <div className="text-[10px] font-mono uppercase tracking-[0.4em] text-gold/80">{t('cp_label_directory')}</div>
            <div className="font-h text-5xl sm:text-6xl tabular-nums text-white">
              {fmt(total, lang)}<span className="text-gold">+</span>
            </div>
            <div className="text-sm font-mono uppercase tracking-widest text-slate-400 inline-flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> {t('cp_dir_coaches_label')}
            </div>
          </div>
          <p className="mt-3 text-base text-slate-300 max-w-2xl">{t('cp_dir_tagline')}</p>
        </section>

        {loading ? (
          <div className="text-center text-slate-400 py-12">{t('cp_dir_loading')}</div>
        ) : coaches.length === 0 ? (
          <div className="rounded-xl border border-gold/15 bg-navy-deep p-8 text-center text-slate-400">
            {t('cp_dir_empty')}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {coaches.map((c) => <CoachCard key={c.id} c={c} lang={lang} />)}
          </div>
        )}
      </main>
    </div>
  );
}
